/**
 * How a mapping's AcroForm name resolves to the field(s) it fills.
 *
 * Split out of `fillPdf.ts` so the name-resolution rule can be consulted without
 * loading `pdf-lib`. The originator's PDF-mapping page asks the same question over
 * a bare set of names (`acroTargetExists`) to warn about handles the selected
 * document lacks, and it runs in the browser — importing it from `fillPdf.ts`
 * would drag the whole PDF-writing library into the front-end bundle for one
 * string comparison. Keeping both functions in this file also makes their
 * agreement structural rather than a comment: when they drifted, every
 * per-option mapping (`Radio#1:MrCheck`) was reported as missing from a PDF it
 * filled perfectly well, so an originator looking at Title or Gender saw four
 * red warnings on four working handles.
 */

/**
 * The `fieldName:exportValue` split, or null when `key` names a field directly.
 *
 * Tries the literal key first — some AcroForm field names legitimately contain
 * colons (permitted by the PDF spec; common in XFA-derived PDFs), so
 * `Namespace:Owner` must keep working as a plain name. Only when the literal name
 * is absent does the colon read as the export-value convention BMO's shared-name
 * checkbox fields use.
 */
export function splitFillKey(
  hasName: (name: string) => boolean,
  key: string,
): { name: string; exportValue: string | null } | null {
  if (hasName(key)) return { name: key, exportValue: null };
  const colonIdx = key.indexOf(':');
  if (colonIdx < 0) return null;
  const name = key.slice(0, colonIdx);
  if (!hasName(name)) return null;
  return { name, exportValue: key.slice(colonIdx + 1) };
}

/**
 * Does `acroFieldName` address a field that a document carrying `names` actually
 * has? The same resolution {@link splitFillKey} performs, expressed over a bare
 * name set so callers that only know the names — the PDF-mapping page, which
 * warns about handles the selected document lacks — can ask the question without
 * a loaded PDFDocument.
 */
export function acroTargetExists(names: ReadonlySet<string>, acroFieldName: string): boolean {
  return splitFillKey((n) => names.has(n), acroFieldName) !== null;
}
